import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AlertCircle } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import UserContext from '../contexts/UserContext';
import { API_BASE_URL } from '../utils/api';

const TacNotification = () => {
  const [showNotification, setShowNotification] = useState(false);
  const [tacInfo, setTacInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useContext(UserContext);
  const location = useLocation();

  const checkTacStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/user/compliance/notifications`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) return;

      const data = await response.json();
      const tacNotification = data.notifications?.find(n => n.notificationType === 'tac' && n.status === 'active');

      if (tacNotification) {
        const tacVersion = tacNotification.title.match(/v?\d+\.\d+/i)?.[0] || `v${tacNotification.notificationId}`;
        const dismissedVersion = localStorage.getItem('tacDismissedVersion');

        if (dismissedVersion !== tacVersion) {
          setTacInfo({
            tacStatus: 'update',
            tacVersion,
            tacMessage: tacNotification.message,
            tacAffectDate: tacNotification.affectDate,
            updatedAt: tacNotification.updatedAt,
            hasUpdate: true,
          });
          setShowNotification(true);
        }
      }
    } catch (error) {
      console.error('Error checking TAC status:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Only check TAC status if:
    // 1. User is logged in AND has a valid token
    // 2. User is on a dashboard page (not homepage or login)
    const token = localStorage.getItem("token");
    const isDashboardPage = location.pathname.includes('-home') || 
                           location.pathname.includes('/admin-') || 
                           location.pathname.includes('/client-') || 
                           location.pathname.includes('/advisor-') || 
                           location.pathname.includes('/liaison-');
    
    if (user && user.email && token && isDashboardPage) {
      checkTacStatus();
    } else {
      setLoading(false);
    }
  }, [user, location.pathname, checkTacStatus]);

  const handleClose = () => {
    // Store the dismissed version so it won't show again
    const currentVersion = tacInfo?.tacVersion || 'v1.0';
    localStorage.setItem('tacDismissedVersion', currentVersion);
    setShowNotification(false);
  };

  if (loading || !showNotification || !tacInfo) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
        <div className="flex items-center mb-4">
          <AlertCircle className="text-yellow-500 mr-2" size={24} />
          <h3 className="text-lg font-semibold text-gray-800">
            Updated Terms and Conditions
          </h3>
        </div>
        
        <div className="mb-4">
          <p className="text-gray-600 mb-3">
            Please note that <strong>Cogent Asset Management Ltd (Cogent Platform)</strong> approved a new version of the Terms and Conditions 
            {tacInfo.tacVersion && <span> ({tacInfo.tacVersion})</span>}.
          </p>
          
          {tacInfo.tacMessage && (
            <div className="bg-blue-50 border-l-4 border-blue-400 p-3 mb-3">
              <p className="text-gray-700 text-sm">
                <strong>Note:</strong> {tacInfo.tacMessage}
              </p>
            </div>
          )}
          
          {tacInfo.tacAffectDate && (
            <p className="text-gray-600 mb-3">
              The edition of the Terms and Conditions will come into effect on <strong>{new Date(tacInfo.tacAffectDate).toLocaleDateString()}</strong>.
            </p>
          )}
          
          <div className="text-sm text-gray-500 space-y-1">
            <p>
              Please find the documents on our <a href="https://cogentplatform.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">website</a>.
            </p>
            <p>
              If you have any questions please contact us at <a href="mailto:operations@cogentplatform.com" className="text-blue-600 hover:underline">operations@cogentplatform.com</a>.
            </p>
          </div>
        </div>
        
        <div className="flex justify-end">
          <button
            onClick={handleClose}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md transition-colors"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

export default TacNotification;
